<script setup lang="ts">
import NewHeader from '~/components/NewHeader.vue';
import NewFooter from '~/components/NewFooter.vue';
import FaceSwap from '@/components/FaceSwap.vue';
import type { MaterialWithStatus } from '~/types';
import { useDisplay } from 'vuetify';

const { t } = useI18n();

useHead({
    title: t('seo.faceSwap.title'),
    meta: [
        {
            name: 'description',
            content: t('seo.faceSwap.description')
        }
    ]
});

const router = useRouter();
const localePath = useLocalePath();
const isShow = ref(true);

const route = useRoute();

const matObj = useState<MaterialWithStatus>('matObj');

// 顶部工具切换：当前在 Face Swap 页（选中态）；AI Pose / AI Video 跳到 editor
const TOOL_TABS = computed<{ id: string; label: string; icon: string; to?: string }[]>(() => [
    { id: 'face-swap', label: t('Face Swap'), icon: 'mdi-face-man-shimmer' },
    { id: 'sex-pose', label: t('AI Pose'), icon: 'mdi-human', to: 'sex-pose' },
    { id: 'ai-video', label: t('AI Video'), icon: 'mdi-video', to: 'ai-video' },
]);
function goTool(tab: { id: string; to?: string }) {
    if (!tab.to) return; // face-swap：当前页，不跳转
    router.push({ path: localePath('/editor'), query: { type: tab.to } });
}

const display = useDisplay();
const isMobile = computed(() => display.xs.value);
</script>

<template>
    <div class="flex flex-col w-full h-screen md:h-auto lg:min-h-screen">
        <NewHeader />
        <div class="container-wrap  mt-[68px] md:mt-[90px] !flex-1 flex flex-col items-center justify-center">
            <div class="inner max-w-[1280px] w-full md:px-8 px-4  flex flex-col items-center mt-6 md:mt-0">
                <!-- 顶部工具切换：移动端等宽铺满(随 .inner 的 px-4 留边)，PC 端居中加大 -->
                <div class="flex w-full md:w-auto gap-2 md:gap-3 mb-5 md:mb-7">
                    <button
                        v-for="tab in TOOL_TABS" :key="tab.id"
                        type="button"
                        class="relative flex flex-1 md:flex-none items-center justify-center gap-1.5 md:gap-2 rounded-full px-3 md:px-7 py-2.5 md:py-3 text-sm md:text-base font-semibold transition"
                        :class="tab.id === 'face-swap'
                            ? 'bg-white text-black'
                            : 'bg-neutral-900 text-white/70 hover:text-white hover:bg-neutral-800'"
                        @click="goTool(tab)"
                    >
                        <span v-if="tab.to" class="new-badge">NEW</span>
                        <v-icon :icon="tab.icon" :size="isMobile ? 18 : 20" />
                        <span class="whitespace-nowrap">{{ tab.label }}</span>
                    </button>
                </div>
                <h1 v-if="isShow"
                    class="px-2 my-4 text-base font-semibold leading-normal text-center text-white md:text-xl lg:text-3xl">
                    {{ t('pages.faceSwap.title') }}
                </h1>
                <FaceSwap @file-selected="(fileSelected) => isShow = !fileSelected" :mat-obj="matObj">
                </FaceSwap>
               

                <template v-if="isShow">
                    <h2 class="hidden text-xl font-semibold leading-7 text-center text-white md:contents">
                        {{ t('pages.faceSwap.howToTitle') }}
                    </h2>
                    <div
                        class="inline-flex gap-3 justify-around items-center py-2 my-4 w-full rounded-xl bg-neutral-900 md:py-4">
                        <div class="inline-flex flex-col gap-1.5 justify-start items-center rounded-xl">
                            <div class="relative w-10 h-10 md:w-14 md:h-14">
                                <img src="/icon/feature_02.svg" alt="">
                            </div>
                            <div class="self-stretch text-xs font-normal text-center text-white/50 md:text-base">
                                <h3 class="hidden md:contents text-neutral-50">{{ t('pages.faceSwap.step1Label') }}:</h3>
                                <h3>{{ t('pages.faceSwap.step1') }}</h3>
                            </div>
                        </div>
                        <div class="inline-flex flex-col gap-1.5 justify-start items-center rounded-xl">
                            <div class="relative w-10 h-10 md:w-14 md:h-14">
                                <img src="/icon/feature_01.svg" alt="">
                            </div>
                            <div class="self-stretch text-xs font-normal text-center text-white/50 md:text-base">
                                <h3 class="hidden md:contents text-neutral-50">{{ t('pages.faceSwap.step2Label') }}:</h3>
                                <h3>{{ t('pages.faceSwap.step2') }}</h3>
                            </div>
                        </div>
                        <div class="inline-flex flex-col gap-1.5 justify-start items-center rounded-xl">
                            <div class="relative w-10 h-10 md:w-14 md:h-14">
                                <img src="/icon/feature_04.svg" alt="">
                            </div>
                            <div class="self-stretch text-xs font-normal text-center text-white/50 md:text-base">
                                <h3 class="hidden md:contents text-neutral-50">{{ t('pages.faceSwap.step3Label') }}:</h3>
                                <h3>{{ t('pages.faceSwap.step3') }}</h3>
                            </div>
                        </div>
                    </div>

                    <div class="inline-flex flex-col justify-center items-start w-full h-52">
                        <h2 class="text-base font-medium leading-normal text-white md:text-xl md:my-6">
                            {{ t('pages.faceSwap.tipsTitle') }}
                        </h2>
                        <div
                            class="flex flex-col gap-y-3 self-stretch text-xs font-normal text-white opacity-50 md:text-base">
                            <span>{{ t('pages.faceSwap.tip1') }}</span>
                            <span>{{ t('pages.faceSwap.tip2') }}</span>
                            <span>{{ t('pages.faceSwap.tip3') }}</span>
                            <span>{{ t('pages.faceSwap.tip4') }}</span>
                        </div>
                    </div>
                </template>

            </div>
        </div>
        <NewFooter v-if="!isMobile" class="mt-20" />
    </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/pages';

/* AI Pose / AI Video 新功能角标 + 轻微脉冲动画 */
.new-badge {
    position: absolute;
    top: -6px;
    right: -4px;
    padding: 3px 5px;
    font-size: 9px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: 0.04em;
    color: #fff;
    background: linear-gradient(135deg, #f97316 0%, #ef4444 100%);
    border-radius: 9999px;
    box-shadow: 0 0 0 2px #0a0a0a;
    animation: newBadgePulse 1.6s ease-in-out infinite;
    pointer-events: none;
}
@keyframes newBadgePulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.14); }
}

/* .error-case img {
    width: 148px !important;
    margin-right: 20px;
} */
</style>
